import React, { useState, useEffect, Suspense, useCallback, useMemo } from 'react';
import { FaPlus, FaDumbbell, FaSave, FaMars, FaVenus } from 'react-icons/fa';
import { facilitiesService, regionsService, facilityTypesService, genderRestrictionsService, daysOfWeek, pricingService } from '../../services/sportsManagementService';
import { Modal, message } from 'antd';
import './SportsManagementStyle.scss';
import FacilityListTable from './FacilityListTable';

// Lazy load the modal component
const FacilityEditModal = React.lazy(() => import('./FacilityEditModal'));

const isFitnessFacilityRecord = (item) =>
    ((item?.typeName || item?.type || item?.name || item?.facilityName || '')).toLowerCase().includes('fitness');

const FacilitiesManagement = ({ userRegions, fitnessAccess = true }) => {
    const [facilities, setFacilities] = useState([]);
    const [regions, setRegions] = useState([]);
    const [facilityTypes, setFacilityTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingFacility, setEditingFacility] = useState(null);

    // Gender Restriction State
    const [showGenderModal, setShowGenderModal] = useState(false);
    const [currentFacilityId, setCurrentFacilityId] = useState(null);
    const [currentFacilityData, setCurrentFacilityData] = useState(null);
    const [genderRestrictions, setGenderRestrictions] = useState([]);
    const [selectedGenderDay, setSelectedGenderDay] = useState(null);
    const [pricingList, setPricingList] = useState([]);

    // Filter state
    const [selectedRegionFilter, setSelectedRegionFilter] = useState('');

    useEffect(() => {
        loadRegions();
        loadFacilityTypes();
        const preloadModal = () => import('./FacilityEditModal');
        preloadModal().catch(err => console.log('Preload failed', err));
    }, []);

    const fetchFacilities = useCallback(async (regionId) => {
        setLoading(true);
        try {
            const res = await facilitiesService.getAll(null, regionId || null);
            if (res?.isSuccess) {
                let fetchedFacilities = res.data;
                if (userRegions && userRegions.length > 0) {
                    const userRole = JSON.parse(localStorage.getItem('user') || '{}').userType?.toLowerCase();
                    if (userRole === 'regionalsports') {
                        const assignedIds = userRegions.map(ur => ur.id);
                        fetchedFacilities = fetchedFacilities.filter(f => assignedIds.includes(f.regionId));
                    }
                }
                if (!fitnessAccess) {
                    fetchedFacilities = fetchedFacilities.filter(f => !isFitnessFacilityRecord(f));
                }
                setFacilities(fetchedFacilities);

                if (fetchedFacilities?.length) {
                    let derivedTypes = fetchedFacilities
                        .filter(f => f.typeId && f.typeName)
                        .reduce((acc, f) => {
                            if (!acc.some(x => String(x.id) === String(f.typeId))) {
                                acc.push({ id: f.typeId, name: f.typeName });
                            }
                            return acc;
                        }, []);

                    if (derivedTypes.length) {
                        if (fitnessAccess && !derivedTypes.some(t => (t.name || '').toLowerCase().includes('fitness'))) {
                            derivedTypes = [...derivedTypes, { id: 7, name: 'Fitness' }];
                        }
                        if (!fitnessAccess) {
                            derivedTypes = derivedTypes.filter(t => !(t.name || '').toLowerCase().includes('fitness'));
                        }
                        setFacilityTypes(prev => (prev && prev.length ? prev : derivedTypes));
                    }
                }
            }
        } catch (error) {
            console.error('Tesisler yüklenemedi:', error);
            message.error('Tesisler yüklenemedi.');
        } finally {
            setLoading(false);
        }
    }, [userRegions]);

    useEffect(() => {
        fetchFacilities(selectedRegionFilter);
    }, [selectedRegionFilter, fetchFacilities]);

    const loadRegions = async () => {
        try {
            const res = await regionsService.getAll(true);
            if (res?.isSuccess) {
                let fetchedRegions = res.data;
                if (userRegions && userRegions.length > 0) {
                    const userRole = JSON.parse(localStorage.getItem('user') || '{}').userType?.toLowerCase();
                    if (userRole === 'regionalsports') {
                        const assignedIds = userRegions.map(ur => ur.id);
                        fetchedRegions = fetchedRegions.filter(r => assignedIds.includes(r.regionId));
                    }
                }
                setRegions(fetchedRegions);
            }
        } catch (error) {
            console.error('Bölgeler yüklenemedi:', error);
            message.error('Bölgeler yüklenemedi.');
        }
    };

    const loadFacilityTypes = async () => {
        try {
            const res = await facilityTypesService.getAll();
            if (res?.isSuccess) {
                const data = res.data || [];
                const hasFitness = data.some(t => (t.name || '').toLowerCase().includes('fitness'));
                const nextTypes = hasFitness ? data : [...data, { id: 7, name: 'Fitness' }];
                setFacilityTypes(fitnessAccess ? nextTypes : nextTypes.filter(t => !(t.name || '').toLowerCase().includes('fitness')));
            }
        } catch (error) {
            if (error?.response?.status === 404) {
                console.warn('Tesis tipleri endpoint bulunamadı, varsayılan tip listesi kullanılacak.');
                const fallbackTypes = [
                    { id: 1, name: 'Tenis' },
                    { id: 2, name: 'Futbol' },
                    { id: 3, name: 'Basketbol' },
                    { id: 4, name: 'Voleybol' },
                    { id: 5, name: 'Basketbol/Voleybol' },
                    { id: 6, name: 'Kapalı Spor Havuzu' },
                    { id: 7, name: 'Fitness' }
                ];
                setFacilityTypes(fitnessAccess ? fallbackTypes : fallbackTypes.filter(t => !(t.name || '').toLowerCase().includes('fitness')));
                return;
            }
            console.error('Tesis tipleri yüklenemedi:', error);
            message.error('Tesis tipleri yüklenemedi.');
        }
    };

    const handleDelete = useCallback(async (id) => {
        Modal.confirm({
            title: 'Onay',
            content: 'Bu tesisi silmek istediğinizden emin misiniz?',
            okText: 'Evet',
            cancelText: 'Hayır',
            onOk: async () => {
                try {
                    await facilitiesService.delete(id);
                    message.success('Tesis silindi.');
                    setFacilities(prev => prev.filter(f => f.facilityId !== id));
                } catch (error) {
                    console.error('Silme hatası:', error);
                    message.error('Tesis silinemedi.');
                }
            }
        });
    }, []);

    const openEditModal = useCallback((facility) => {
        setEditingFacility(facility);
        setShowModal(true);
    }, []);

    // Generate time slots from facility working hours or pricing rules
    const generateTimeSlots = (facility, dayId, rules = []) => {
        if (!facility) return [];

        // Check if there are specific rules for this day
        // rules[i].dayOfWeek === null means "Her Gün"
        const dayRules = rules.filter(r => {
            const rDay = r.dayOfWeek !== undefined ? r.dayOfWeek : r.DayOfWeek;
            return rDay === dayId || rDay === null;
        });

        if (dayRules.length > 0) {
            const slots = dayRules.map(r => ({
                start: r.startTime || r.StartTime,
                end: r.endTime || r.EndTime
            })).sort((a, b) => a.start.localeCompare(b.start));

            // Deduplicate for shared facilities with overlapping sport-specific rules
            return slots.filter((slot, index, self) =>
                index === self.findIndex((s) => s.start === slot.start && s.end === slot.end)
            );
        }

        const startStr = facility.workingHoursStart || facility.WorkingHoursStart || '08:00';
        const endStr = facility.workingHoursEnd || facility.WorkingHoursEnd || '23:59';
        const duration = facility.slotDurationInMinutes || facility.SlotDurationInMinutes || 60;

        const parseTime = (str) => {
            const [h, m] = str.split(':').map(Number);
            return h * 60 + m;
        };

        let startMinutes = parseTime(startStr);
        let endMinutes = parseTime(endStr);
        if (endMinutes === 0 || endMinutes === 1440) endMinutes = 1440; // 00:00 -> 24:00

        const slots = [];
        for (let m = startMinutes; m + duration <= endMinutes; m += duration) {
            const sH = Math.floor(m / 60) % 24;
            const sM = m % 60;
            const eM_total = m + duration;
            const eH = Math.floor(eM_total / 60) % 24;
            const eMn = eM_total % 60;
            const start = `${String(sH).padStart(2, '0')}:${String(sM).padStart(2, '0')}`;
            const end = (eH === 0 && eMn === 0) ? '00:00' : `${String(eH).padStart(2, '0')}:${String(eMn).padStart(2, '0')}`;
            slots.push({ start, end });
        }
        return slots;
    };

    const openGenderModal = useCallback(async (facility) => {
        setCurrentFacilityId(facility.facilityId);
        setCurrentFacilityData(facility);
        setGenderRestrictions([]);
        setPricingList([]);
        setSelectedGenderDay(null);

        try {
            const [genderRes, pricingRes] = await Promise.all([
                genderRestrictionsService.getByFacility(facility.facilityId),
                pricingService.getByFacility(facility.facilityId)
            ]);

            if (genderRes?.isSuccess) {
                setGenderRestrictions(genderRes.data || []);
            }
            if (pricingRes?.isSuccess) {
                setPricingList(pricingRes.data || []);
            }

            setSelectedGenderDay(0); // Pazar default
            setShowGenderModal(true);
        } catch (error) {
            console.error('Yükleme hatası:', error);
            message.error('Veriler yüklenemedi.');
        }
    }, []);

    const handleModalSuccess = useCallback(() => {
        setShowModal(false);
        setEditingFacility(null);
        window.location.reload();
    }, []);

    const handleGenderSave = async (dayOfWeek, startTime, endTime, allowedGender) => {
        try {
            const payload = {
                facilityId: currentFacilityId,
                dayOfWeek,
                startTime,
                endTime,
                allowedGender
            };
            await genderRestrictionsService.save(payload);

            setGenderRestrictions(prev => {
                const filtered = prev.filter(p => {
                    const pDay = p.dayOfWeek !== undefined ? p.dayOfWeek : p.DayOfWeek;
                    const pStart = p.startTime || p.StartTime;
                    return !(pDay === dayOfWeek && pStart === startTime);
                });
                if (allowedGender !== 'All') {
                    filtered.push({ dayOfWeek, startTime, endTime, allowedGender, isActive: true });
                }
                return filtered;
            });
        } catch (error) {
            console.error('Kaydetme hatası:', error);
            message.error('Kaydetme başarısız.');
        }
    };

    const getSlotGender = (dayOfWeek, startTime) => {
        const match = genderRestrictions.find(r => {
            const rDay = r.dayOfWeek !== undefined ? r.dayOfWeek : r.DayOfWeek;
            const rStart = r.startTime || r.StartTime;
            return rDay === dayOfWeek && rStart === startTime;
        });
        return match ? (match.allowedGender || match.AllowedGender) : 'All';
    };

    const getDayRestrictionCounts = (dayId) => {
        const dayRestrictions = genderRestrictions.filter(r => {
            const rDay = r.dayOfWeek !== undefined ? r.dayOfWeek : r.DayOfWeek;
            const rGender = r.allowedGender || r.AllowedGender;
            return rDay === dayId && rGender !== 'All';
        });
        const maleCount = dayRestrictions.filter(r => (r.allowedGender || r.AllowedGender) === 'Male').length;
        const femaleCount = dayRestrictions.filter(r => (r.allowedGender || r.AllowedGender) === 'Female').length;
        return { maleCount, femaleCount, total: maleCount + femaleCount };
    };

    const timeSlots = useMemo(() => {
        return generateTimeSlots(currentFacilityData, selectedGenderDay, pricingList);
    }, [currentFacilityData, selectedGenderDay, pricingList]);

    return (
        <div className="sports-management-page">
            <div className="page-header">
                <h1><FaDumbbell /> Spor Tesisleri Yönetimi</h1>
                <button className="btn-premium" onClick={() => { setEditingFacility(null); setShowModal(true); }}>
                    <FaPlus /> Yeni Tesis Ekle
                </button>
            </div>

            <div className="premium-card">
                <div className="card-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', width: '100%' }}>
                        <h3>Tesisler</h3>
                        <div className="form-group" style={{ marginBottom: 0, marginLeft: 'auto', minWidth: '250px' }}>
                            <select
                                value={selectedRegionFilter}
                                onChange={(e) => setSelectedRegionFilter(e.target.value)}
                            >
                                <option value="">Tüm Bölgeler</option>
                                {regions.map(r => (
                                    <option key={r.regionId} value={r.regionId}>{r.regionName}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
                <div className="card-body">
                    {loading ? (
                        <div className="loading-spinner">Yükleniyor...</div>
                    ) : (
                        <FacilityListTable
                            facilities={facilities}
                            openEditModal={openEditModal}
                            openGenderModal={openGenderModal}
                            handleDelete={handleDelete}
                        />
                    )}
                </div>
            </div>

            <Suspense fallback={<div className="modal-overlay"><div className="loading-spinner" style={{ color: 'white' }}>Yükleniyor...</div></div>}>
                <FacilityEditModal
                    show={showModal}
                    onClose={() => setShowModal(false)}
                    editingFacility={editingFacility}
                    regions={regions}
                    facilityTypes={facilityTypes}
                    onSuccess={handleModalSuccess}
                />
            </Suspense>

            {showGenderModal && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '700px' }}>
                        <div className="modal-header">
                            <h2>Cinsiyet Kısıtlamaları — Saatlik</h2>
                            <button className="close-btn" onClick={() => setShowGenderModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                            <p style={{ marginBottom: '1rem', color: '#666', fontSize: '0.9rem' }}>
                                Bir gün seçin, ardından saatlere cinsiyet atayın.
                            </p>

                            {/* Day Selector */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
                                {daysOfWeek.map(day => {
                                    const counts = getDayRestrictionCounts(day.id);
                                    const isSelected = selectedGenderDay === day.id;
                                    return (
                                        <button
                                            key={day.id}
                                            onClick={() => setSelectedGenderDay(day.id)}
                                            style={{
                                                padding: '10px 16px',
                                                borderRadius: '10px',
                                                border: isSelected ? '2px solid #3b82f6' : '1px solid #d1d5db',
                                                background: isSelected ? '#eff6ff' : '#fff',
                                                color: isSelected ? '#1d4ed8' : '#374151',
                                                fontWeight: '600',
                                                fontSize: '0.9rem',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                position: 'relative'
                                            }}
                                        >
                                            {day.name}
                                            {counts.total > 0 && (
                                                <span style={{
                                                    display: 'inline-flex',
                                                    gap: '4px',
                                                    marginLeft: '8px',
                                                    fontSize: '0.75rem'
                                                }}>
                                                    {counts.maleCount > 0 && (
                                                        <span style={{ color: '#3b82f6' }}>♂{counts.maleCount}</span>
                                                    )}
                                                    {counts.femaleCount > 0 && (
                                                        <span style={{ color: '#ec4899' }}>♀{counts.femaleCount}</span>
                                                    )}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Time Slots for Selected Day */}
                            {selectedGenderDay !== null && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <h4 style={{ margin: '0 0 12px', fontSize: '1rem', color: '#374151' }}>
                                        {daysOfWeek.find(d => d.id === selectedGenderDay)?.name} — Saat Kısıtlamaları
                                    </h4>
                                    {timeSlots.map(slot => {
                                        const currentGender = getSlotGender(selectedGenderDay, slot.start);
                                        const bgColor = currentGender === 'Male' ? '#eff6ff'
                                            : currentGender === 'Female' ? '#fdf2f8'
                                                : '#f9fafb';
                                        const borderColor = currentGender === 'Male' ? '#93c5fd'
                                            : currentGender === 'Female' ? '#f9a8d4'
                                                : '#e5e7eb';
                                        return (
                                            <div key={slot.start} style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '10px 14px',
                                                backgroundColor: bgColor,
                                                borderRadius: '10px',
                                                border: `1.5px solid ${borderColor}`,
                                                transition: 'all 0.2s'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    {currentGender === 'Male' && <FaMars style={{ color: '#3b82f6', fontSize: '1.1rem' }} />}
                                                    {currentGender === 'Female' && <FaVenus style={{ color: '#ec4899', fontSize: '1.1rem' }} />}
                                                    <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#111827' }}>
                                                        {slot.start} - {slot.end}
                                                    </span>
                                                </div>
                                                <select
                                                    value={currentGender}
                                                    onChange={(e) => handleGenderSave(selectedGenderDay, slot.start, slot.end, e.target.value)}
                                                    style={{
                                                        padding: '6px 10px',
                                                        borderRadius: '8px',
                                                        border: '1px solid #d1d5db',
                                                        fontSize: '0.85rem',
                                                        fontWeight: '600',
                                                        cursor: 'pointer',
                                                        background: '#fff',
                                                        color: currentGender === 'Male' ? '#2563eb'
                                                            : currentGender === 'Female' ? '#db2777'
                                                                : '#6b7280'
                                                    }}
                                                >
                                                    <option value="All">Herkese Açık</option>
                                                    <option value="Male">Sadece Erkek</option>
                                                    <option value="Female">Sadece Kadın</option>
                                                </select>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn-premium btn-outline" onClick={() => setShowGenderModal(false)}>Kapat</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FacilitiesManagement;
